import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getFeaturedWorks } from '@/utils/content';

export default function HeroCarousel() {
  const items = getFeaturedWorks();
  const [active, setActive] = useState(0);

  const next = useCallback(() => {
    setActive((prev) => (prev + 1) % items.length);
  }, [items.length]);

  const prev = () => {
    setActive((prev) => (prev - 1 + items.length) % items.length);
  };

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [items.length, next]);

  if (items.length === 0) {
    return (
      <div className="flex h-[420px] items-center justify-center bg-primary-900 text-background-200 md:h-[600px]">
        暂无内容，敬请期待
      </div>
    );
  }

  const current = items[active];

  return (
    <section className="relative h-[460px] w-full overflow-hidden bg-primary-950 md:h-[600px]">
      {items.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === active ? 'opacity-100' : 'pointer-events-none opacity-0'
          }`}
        >
          <img
            src={item.backdrop}
            alt={`${item.title} 主视觉`}
            className={`h-full w-full object-top ${index === active ? 'animate-slow-zoom' : ''}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />

      <div className="relative z-10 mx-auto flex h-full w-full items-center px-4 md:px-6">
        <div key={current.id} className="w-full max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
            <i className="ri-fire-fill" aria-hidden="true" />
            本周热播
          </span>
          <h1 className="mt-4 font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">
            {current.title}
          </h1>
          <p className="mt-2 text-sm uppercase tracking-[0.18em] text-background-300">{current.subtitle}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1 font-semibold text-accent-400">
              <i className="ri-star-fill" aria-hidden="true" />
              {current.rating.toFixed(1)}
            </span>
            <span className="h-1 w-1 rounded-full bg-background-400" aria-hidden="true" />
            <span>{current.year}</span>
            <span className="h-1 w-1 rounded-full bg-background-400" aria-hidden="true" />
            <span>{current.region}</span>
            <span className="h-1 w-1 rounded-full bg-background-400" aria-hidden="true" />
            <span>{current.typeLabel}</span>
          </div>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {current.synopsis}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-400"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true" />
              查看剧情介绍
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300/50 px-5 py-2.5 text-sm font-medium text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400"
            >
              更多{current.typeLabel}
            </Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 z-10 flex items-center gap-3 md:right-6">
        <button
          type="button"
          onClick={prev}
          aria-label="上一张"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/40 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400 md:h-10 md:w-10"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={next}
          aria-label="下一张"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300/40 text-background-100 transition-colors hover:border-accent-400 hover:text-accent-400 md:h-10 md:w-10"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true" />
        </button>
      </div>

      <div className="absolute bottom-6 left-4 z-10 flex items-center gap-2 md:left-6">
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            aria-label={`切换到 ${item.title}`}
            onClick={() => setActive(index)}
            className={`h-1.5 rounded-full transition-all ${
              index === active ? 'w-8 bg-accent-500' : 'w-3 bg-background-400/60 hover:bg-background-300'
            }`}
          />
        ))}
      </div>
    </section>
  );
}